import React, { useContext, useEffect, useState } from "react";
import { ArrowRight, Heart, ShoppingCart } from "lucide-react";
import "../../../../assets/css/user/card/TpDealCard/TpDealCard.css";
import AppContext from "../../../../context/AppContext";
import { FiHeart, FiMapPin, FiShoppingCart } from "react-icons/fi";
import { FaCheck, FaStar, FaHeart } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Col } from "react-bootstrap";

// ✅ Countdown hook — sirf expiresAt se related, kuch aur nahi
const useCountdown = (expiresAt) => {
  const calcLeft = () => {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt) - new Date();
    return diff > 0 ? diff : 0;
  };

  const [msLeft, setMsLeft] = useState(calcLeft);

  useEffect(() => {
    if (!expiresAt) return;
    const interval = setInterval(() => {
      const left = calcLeft();
      setMsLeft(left);
      if (left === 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (msLeft === null) return null;
  if (msLeft === 0) return "Expired";

  const totalSecs = Math.floor(msLeft / 1000);
  const days = Math.floor(totalSecs / 86400);
  const hrs = Math.floor((totalSecs % 86400) / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;

  if (days > 0) return `${days}d ${hrs}h ${mins}m`;
  if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
  return `${mins}m ${secs}s`;
};

const TpDealCard = ({
  deal,
  isAlreadyInCart = false,
  isAlreadyInWishlist = false,
  onRemove,
  getCartItems,
  screenName = "",
}) => {
  const { baseUrl, baseUrlForFiles } = useContext(AppContext);
  const navigate = useNavigate();
  const [isInCart, setIsInCart] = useState(isAlreadyInCart);
  const [isInWishlist, setIsInWishlist] = useState(isAlreadyInWishlist); // ✅ sirf ek declaration
  const [isProcessing, setIsProcessing] = useState(false);
  const formatPakistanDateTime = (dateTime) => {
    if (!dateTime) return "—";

    const date = new Date(dateTime);

    if (isNaN(date.getTime())) return "—";

    return date.toLocaleString("en-PK", {
      timeZone: "Asia/Karachi",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };
  const countdown = useCountdown(deal?.expires_at);

  const isInstantDeal = deal?.deal_type === "Instant Deals";

  const isNotStarted =
    isInstantDeal && deal?.start_at && new Date(deal.start_at) > new Date();

  const isSold = deal?.status === "Sold";

  const isExpired = countdown === "Expired";

  const isUnavailable = isSold || isExpired;

  const BadgeIcon = ({ type }) => {
    if (type === "instant") {
      return (
        <svg viewBox="0 0 24 24" fill="none" width="11" height="11">
          <path
            d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="white"
          />
        </svg>
      );
    }
    if (type === "hot") {
      return (
        <svg viewBox="0 0 24 24" fill="none" width="11" height="11">
          <path
            d="M12 2C8 2 5 6 5 10c0 3 1.5 5.5 4 7l1 4h4l1-4c2.5-1.5 4-4 4-7 0-4-3-8-7-8z"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="10" r="2" fill="white" />
        </svg>
      );
    }
    if (type === "trending") {
      return (
        <svg viewBox="0 0 24 24" fill="none" width="11" height="11">
          <polyline
            points="22 7 13.5 15.5 8.5 10.5 2 17"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points="16 7 22 7 22 13"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    return null;
  };

  const AddToCart = (deal) => {
    if (isProcessing || isInCart) return;

    const savedCustomer = JSON.parse(localStorage.getItem("loggedInUser"));

    if (!savedCustomer) {
      Swal.fire({
        icon: "info",
        title: "Login Required",
        text: "Please sign up or log in to add deals to your cart.",
        confirmButtonText: "Sign Up",
        showCancelButton: true,
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/signup");
        }
      });
      return;
    }
    setIsProcessing(true);

    const formData = new FormData();
    formData.append("tpcustomer_id", savedCustomer.id);
    formData.append("tpdeal_id", deal.id);

    axios
      .post(`${baseUrl}/customer/cart/addToCart`, formData, {
        headers: { "Content-Type": "multipart/form-data" }, // ✅ typo fix (pehle form-Data tha)
      })
      .then((response) => {
        setIsInCart(true);
        window.dispatchEvent(new Event("cartUpdated"));
        navigate("/");
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Deal Added To Cart Successfully",
          confirmButtonText: "Okay",
        });
      })
      .catch((error) => {
        console.log(error.data);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong. Please try again.",
        });
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  const RemoveFromCart = (deal) => {
    if (isProcessing) return;
    const savedCustomer = JSON.parse(localStorage.getItem("loggedInUser"));
    if (!savedCustomer) return;

    const formData = new FormData();
    formData.append("tpcustomer_id", savedCustomer.id);
    formData.append("tpdeal_id", deal.id);
    setIsProcessing(true);

    axios
      .post(`${baseUrl}/customer/cart/removeFromCart`, formData, {
        headers: { "Content-Type": "multipart/form-data" }, // ✅ typo fix
      })
      .then((response) => {
        setIsInCart(false);
        window.dispatchEvent(new Event("cartUpdated"));
        if (onRemove) onRemove();
        Swal.fire({
          icon: "success",
          title: "Removed",
          text: "Deal Removed From Cart",
          confirmButtonText: "Okay",
        });
        getCartItems();
      })
      .catch((error) => {
        console.log(error.data);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  // ✅ Deal ki wishlist mein hone ki status API se fetch karo
  const checkWishlistStatus = () => {
    const savedCustomer = JSON.parse(localStorage.getItem("loggedInUser"));

    if (!savedCustomer?.id || !deal?.id) return;

    const formData = new FormData();
    formData.append("tpcustomer_id", savedCustomer.id);

    axios
      .post(`${baseUrl}/customer/wishlist/viewWishlist`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        console.log("Wishlist API:", res.data);
        console.log("Current Deal ID:", deal.id);

        const wishlist = res.data?.wishlist || [];

        const isWishlisted = wishlist.some((item) => {
          const wishlistDealId = item.tpdeal_id ?? item.tpdeal?.id;

          console.log("Wishlist Deal ID:", wishlistDealId);

          return String(wishlistDealId) === String(deal.id);
        });

        console.log("Is Wishlisted:", isWishlisted);

        setIsInWishlist(isWishlisted);
      })
      .catch((error) => {
        console.log("Wishlist status error:", error);
      });
  };

  const AddToWishlist = async (deal) => {
    // =====================================================
    // DOUBLE CLICK / MULTIPLE REQUEST PREVENT
    // =====================================================
    if (isProcessing) {
      return;
    }

    const savedCustomer = JSON.parse(localStorage.getItem("loggedInUser"));

    // =====================================================
    // LOGIN CHECK
    // =====================================================
    if (!savedCustomer?.id) {
      Swal.fire({
        icon: "info",
        title: "Login Required",
        text: "Please sign up or log in to add deals to your wishlist.",
        confirmButtonText: "Sign Up",
        showCancelButton: true,
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/signup");
        }
      });

      return;
    }

    // =====================================================
    // DEAL ID CHECK
    // =====================================================
    if (!deal?.id) {
      console.error("Deal ID is missing:", deal);
      return;
    }

    try {
      // IMPORTANT
      // Request start hone se pehle processing true
      setIsProcessing(true);

      const formData = new FormData();

      formData.append("tpcustomer_id", savedCustomer.id);

      formData.append("tpdeal_id", deal.id);

      console.log("Wishlist Request:", {
        customer_id: savedCustomer.id,
        deal_id: deal.id,
      });

      const response = await axios.post(
        `${baseUrl}/customer/wishlist/addToWishlist`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      console.log("Wishlist Toggle Response:", response.data);

      const action = String(response.data?.action || "").toLowerCase();

      // =====================================================
      // ADDED
      // =====================================================
      // Agar backend action:"added" bhej raha hai
      // ya wishlist object bhej raha hai, dono ko added samjho.
      if (action === "added" || response.data?.wishlist) {
        setIsInWishlist(true);

        window.dispatchEvent(new Event("wishlistUpdated"));

        Swal.fire({
          icon: "success",
          title: "Added",
          text: "Deal added to wishlist",
          timer: 1500,
          showConfirmButton: false,
        });

        return;
      }

      // =====================================================
      // REMOVED
      // =====================================================
      if (action === "removed") {
        setIsInWishlist(false);

        window.dispatchEvent(new Event("wishlistUpdated"));

        if (screenName === "wishlist" && onRemove) {
          onRemove();
        }

        Swal.fire({
          icon: "success",
          title: "Removed",
          text: "Deal removed from wishlist",
          timer: 1500,
          showConfirmButton: false,
        });

        return;
      }
      // =====================================================
      // UNKNOWN RESPONSE
      // =====================================================

      console.error("Unexpected wishlist response:", response.data);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: response.data?.message || "Wishlist update failed.",
      });
    } catch (error) {
      console.error("Wishlist Error:", error?.response?.data || error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
      });
    } finally {
      // Request complete hone ke baad unlock
      setIsProcessing(false);
    }
  };
  // ✅ Mount hote hi wishlist status check karo (agar prop se nahi diya gaya to)
  useEffect(() => {
    if (screenName === "wishlist") {
      setIsInWishlist(true);
      return;
    }

    // checkWishlistStatus();
  }, [deal?.id, screenName]);
  // Sold ya Expired deal customer ko show nahi hogi
  if (isSold || isExpired) {
    return null;
  }

  return (
    <Col xxl={3} xl={3} lg={4} md={6} sm={10} xs={12} key={deal.id}>
      <div
        className="td-card"
        key={deal?.id}
        onClick={() => navigate(`/deal-details/${deal.id}`)}
        style={{ cursor: "pointer" }}
      >
        <div className="td-card-banner">
          <img
            src={`${baseUrlForFiles}/${deal?.deal_image}`}
            alt={deal?.title}
            className="td-card-img"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentElement.classList.add("td-card-banner--fallback");
            }}
          />

          <div className="td-card-overlay" />

          <span
            className={`td-hot-badge ${
              deal?.deal_type === "Hot Deals"
                ? "td-badge-hot"
                : deal?.deal_type === "Instant Deals"
                  ? "td-badge-instant"
                  : deal?.deal_type === "Trending Deals"
                    ? "td-badge-trending"
                    : ""
            }`}
          >
            {deal?.deal_type === "Hot Deals" && "🔥 Hot"}
            {deal?.deal_type === "Instant Deals" && "⚡ Instant"}
            {deal?.deal_type === "Trending Deals" && "📈 Trending"}
          </span>
          <span className="td-discount-badge">
            {deal?.discount_percentage}% OFF
          </span>

          <button
            type="button"
            className={`td-wishlist-btn ${
              isInWishlist ? "td-wishlist-btn--active" : ""
            }`}
            disabled={isProcessing}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (isProcessing) return;

              AddToWishlist(deal);
            }}
          >
            {isInWishlist ? <FaHeart /> : <FiHeart />}
          </button>

          {countdown && (
            <div
              className={`td-timer-badge ${isExpired ? "td-timer-badge--expired" : ""}`}
            >
              {isExpired ? (
                "⛔ Expired"
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="#4f46e5"
                      strokeWidth="5"
                    />
                    <polyline
                      points="12 7 12 12 15 14"
                      stroke="#4f46e5"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  {countdown}
                </>
              )}
            </div>
          )}
        </div>

        <div className="td-card-body">
          <p className="td-meta">
            <span className="td-category text-success text-bold">
              {deal?.tpcategory?.categoryName}
            </span>
            <span className="td-dot">·</span>
            <span className="td-shop">{deal?.shop}</span>
          </p>

          <h3 className="td-deal-title">{deal?.deal_title}</h3>

          {isInstantDeal && deal?.start_at && (
            <div className="td-location">
              <span className="td-location-icon">🕐</span>

              <span className="td-location-text">
                {isNotStarted ? (
                  <>Starts: {formatPakistanDateTime(deal.start_at)}</>
                ) : (
                  <>Started: {formatPakistanDateTime(deal.start_at)}</>
                )}
              </span>
            </div>
          )}

          <div className="td-feature">
            {(() => {
              let services = [];
              try {
                const parsed =
                  typeof deal?.services === "string"
                    ? JSON.parse(deal.services)
                    : deal?.services;
                if (Array.isArray(parsed)) {
                  services = parsed.filter((s) => s?.itemname);
                }
              } catch (e) {
                services = [];
              }

              if (services.length === 0) return null;

              return services.map((s, idx) => (
                <span key={idx} className="td-feature-item">
                  <FaCheck />
                  <span>
                    {s.itemname}
                    {s.price ? ` – Rs.${s.price}` : ""}
                  </span>
                </span>
              ));
            })()}
          </div>

          {deal?.deal_rating && (
            <div className="td-rating">
              <FaStar />
              <span className="td-rating-val">{deal?.deal_rating}</span>
              <span className="td-reviews">({deal?.deal_reviews})</span>
            </div>
          )}

          <div className="td-pricing">
            <span className="td-original">Rs.{deal?.actual_price}</span>
            <span className="td-price">Rs.{deal?.deal_price}</span>
          </div>
        </div>

        <button
          type="button"
          className={`td-deal-btn ${isInCart ? "td-deal-btn--remove" : ""} ${isExpired ? "td-deal-btn--expired" : ""}`}
          disabled={isUnavailable || isProcessing}
          onClick={(e) => {
            e.stopPropagation();
            isInCart ? RemoveFromCart(deal) : AddToCart(deal);
          }}
        >
          <FiShoppingCart />
          {isSold
            ? "Sold Out"
            : isExpired
              ? "Deal Expired"
              : isProcessing
                ? "Please wait..."
                : isInCart
                  ? "Remove from Cart"
                  : "Add to Cart"}
        </button>
      </div>
    </Col>
  );
};

export default TpDealCard;
